import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';
import { BindingGateStatus } from '@/components/binding/BindingGateStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 60_000;

// One check per row. Threshold logic is tiny: 2xx = ok, anything else degraded.
const CHECKS = [
  { id: 'api',   label: 'API gateway',         path: '/api/ping' },
  { id: 'db',    label: 'Database',  path: '/api/health' },
  { id: 'chain', label: 'Base mainnet read',   path: '/api/health' },
];

function statusFromCheck(check, result) {
  if (!result) return 'pending';
  if (check.id === 'api')   return result.ok ? 'ok' : 'down';
  if (check.id === 'db')    return result.ok ? 'ok' : 'down';
  if (check.id === 'chain') {
    if (!result.ok) return 'down';
    const block = result.body?.current_block;
    return typeof block === 'number' && block > 0 ? 'ok' : 'degraded';
  }
  return result.ok ? 'ok' : 'down';
}

function overallStatus(results) {
  const vals = Object.values(results);
  if (vals.some((v) => v === 'down')) return 'down';
  if (vals.some((v) => v === 'degraded')) return 'degraded';
  if (vals.every((v) => v === 'ok')) return 'ok';
  return 'pending';
}

const STATUS_META = {
  ok:       { label: 'All systems operational', color: 'text-green-400',  Icon: CheckCircle2, dot: 'bg-green-400' },
  degraded: { label: 'Partially degraded',       color: 'text-yellow-400', Icon: AlertTriangle, dot: 'bg-yellow-400' },
  down:     { label: 'Service disruption',       color: 'text-red-400',    Icon: XCircle, dot: 'bg-red-400' },
  pending:  { label: 'Checking…',                color: 'text-gray-400',   Icon: RefreshCw, dot: 'bg-gray-500' },
};

export default function StatusPage() {
  const [raw, setRaw] = useState({});
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [honest, setHonest] = useState(null);    // /api/honest-status/live response
  const [honestErr, setHonestErr] = useState(null);
  const [indexer, setIndexer] = useState(null);   // /api/indexer/status response
  const [kypaStats, setKypaStats] = useState(null);   // /api/kypa/stats response
  const [verifyPulse, setVerifyPulse] = useState(null);   // /api/kypa/credential/metrics/pulse

  const runChecks = async () => {
    setRefreshing(true);
    const out = {};
    await Promise.all(
      CHECKS.map(async (c) => {
        const t0 = performance.now();
        try {
          const res = await fetch(`${API_URL}${c.path}`, { method: 'GET' });
          const body = await res.json().catch(() => ({}));
          out[c.id] = { ok: res.ok, status: res.status, body, latencyMs: Math.round(performance.now() - t0) };
        } catch (e) {
          out[c.id] = { ok: false, status: 0, body: { error: String(e) }, latencyMs: Math.round(performance.now() - t0) };
        }
      }),
    );
    setRaw(out);
    setLastCheckedAt(new Date());
    setRefreshing(false);

    // Pull the rich honest-status truth feed in parallel.
    try {
      const r = await fetch(`${API_URL}/api/honest-status/live`, { cache: 'no-store' });
      if (r.ok) {
        setHonest(await r.json());
        setHonestErr(null);
      } else {
        setHonestErr(`HTTP ${r.status}`);
      }
    } catch (e) {
      setHonestErr(String(e).slice(0, 120));
    }

    // Pull on-chain indexer state.
    try {
      const r = await fetch(`${API_URL}/api/indexer/status`, { cache: 'no-store' });
      if (r.ok) setIndexer(await r.json());
    } catch { /* ignore */ }

    // Pull KYPA directory counters.
    try {
      const r = await fetch(`${API_URL}/api/kypa/stats`, { cache: 'no-store' });
      if (r.ok) setKypaStats(await r.json());
    } catch { /* ignore */ }

    // Pull verify-endpoint demand signal (Mastercard-swipe analog).
    try {
      const r = await fetch(`${API_URL}/api/kypa/credential/metrics/pulse`, { cache: 'no-store' });
      if (r.ok) setVerifyPulse(await r.json());
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const id = setTimeout(runChecks, 0);
    const t = setInterval(runChecks, REFRESH_MS);
    return () => { clearTimeout(id); clearInterval(t); };
  }, []);

  const statuses = Object.fromEntries(CHECKS.map((c) => [c.id, statusFromCheck(c, raw[c.id])]));
  const overall = overallStatus(statuses);
  const { Icon, color, label, dot } = STATUS_META[overall];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      <section className="pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-xs tracking-[0.25em] text-gray-400 uppercase mb-3">Live system status</div>

            <div className="flex items-center gap-4 mb-3">
              <Icon className={`w-10 h-10 ${color}`} />
              <h1 className={`text-4xl md:text-5xl font-bold ${color}`} data-testid="status-overall">
                {label}
              </h1>
            </div>

            <div className="text-sm text-gray-500 flex items-center gap-3">
              <span>
                Last checked:{' '}
                {lastCheckedAt
                  ? lastCheckedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                  : '-'}
              </span>
              <span>·</span>
              <span>Auto-refreshing every 60 s</span>
              <button
                onClick={runChecks}
                disabled={refreshing}
                className="ml-2 text-blue-400 hover:underline disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="status-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </motion.div>

          <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="status-checks">
            {CHECKS.map((c, i) => {
              const r = raw[c.id];
              const s = statuses[c.id];
              const meta = STATUS_META[s];
              return (
                <div
                  key={c.id}
                  className={`flex items-center justify-between px-6 py-5 ${i > 0 ? 'border-t border-white/5' : ''}`}
                  data-testid={`status-row-${c.id}`}
                >
                  <div className="flex items-center gap-4">
                    <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
                    <div>
                      <div className="font-medium">{c.label}</div>
                      <div className="text-xs text-gray-500">{c.path}</div>
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    <div className={`font-medium ${meta.color}`}>{meta.label.split(' ').slice(-1)[0]}</div>
                    <div className="text-xs text-gray-500">
                      {r ? <>HTTP {r.status || '-'} · {r.latencyMs} ms</> : '-'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {raw.chain?.body?.current_block ? (
            <div className="mt-6 text-xs text-gray-500" data-testid="status-block">
              Base mainnet current block:{' '}
              <span className="font-mono text-gray-300">{raw.chain.body.current_block.toLocaleString()}</span>
            </div>
          ) : null}

          <BindingGateStatus />

          {/* ── Live protocol data (indexer + packets + directory) ─────────── */}
          <div className="mt-12" data-testid="protocol-data-section">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-sm font-semibold text-white/90 uppercase tracking-[0.18em]">
                Live protocol data
              </h2>
              <span className="text-[10px] text-white/40">
                Backed by <code className="text-white/55">/api/indexer/status</code>, <code className="text-white/55">/api/kypa/stats</code>
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="protocol-counters">
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Slash proofs</div>
                <div className="text-2xl font-bold text-white tabular-nums" data-testid="counter-packets">
                  {indexer ? indexer.total_slash_packets : '-'}
                </div>
                <div className="text-[10px] text-gray-500 mt-1">signed &amp; verifiable</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Indexed events</div>
                <div className="text-2xl font-bold text-white tabular-nums" data-testid="counter-events">
                  {indexer ? indexer.total_events : '-'}
                </div>
                <div className="text-[10px] text-gray-500 mt-1">across every hook network</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Pledged agents</div>
                <div className="text-2xl font-bold text-emerald-400 tabular-nums" data-testid="counter-agents">
                  {kypaStats ? kypaStats.total_agents : '-'}
                </div>
                <div className="text-[10px] text-gray-500 mt-1">
                  ${kypaStats?.total_pledged_usdc ? kypaStats.total_pledged_usdc.toLocaleString() : '-'} pledged
                </div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Recourse paid</div>
                <div className="text-2xl font-bold text-white tabular-nums" data-testid="counter-slashed">
                  ${indexer ? indexer.total_recourse_paid_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                </div>
                <div className="text-[10px] text-gray-500 mt-1">
                  {indexer?.events_by_type?.Slashed ?? '-'} slash{indexer?.events_by_type?.Slashed === 1 ? '' : 'es'} on-chain
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="indexer-networks">
              <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
                <div className="text-xs font-semibold text-white/85 uppercase tracking-[0.15em]">
                  On-chain indexer &middot; per network
                </div>
                <div className="text-[10px] text-white/40">
                  Polls every {indexer?.poll_interval_seconds ?? '-'}s
                </div>
              </div>
              {indexer?.networks?.length ? (
                indexer.networks.map((n) => {
                  const behind = n.blocks_behind;
                  const laggy = behind !== null && behind !== undefined && behind > 25;
                  const dotClass = !n.configured
                    ? 'bg-slate-500'
                    : !n.rpc_connected
                    ? 'bg-rose-400'
                    : laggy
                    ? 'bg-yellow-400'
                    : 'bg-emerald-400';
                  const stateLabel = !n.configured
                    ? 'not configured'
                    : !n.rpc_connected
                    ? 'RPC down'
                    : laggy
                    ? `catching up · ${behind?.toLocaleString?.() ?? behind} blocks behind`
                    : `synced${behind !== null && behind !== undefined ? ` · ${behind} block${behind === 1 ? '' : 's'} behind` : ''}`;
                  return (
                    <div
                      key={n.name}
                      className="px-5 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6 border-t border-white/[0.06] first:border-t-0"
                      data-testid={`indexer-network-${n.name}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className={`w-2 h-2 rounded-full ${dotClass} ${!laggy && n.configured && n.rpc_connected ? 'shadow-[0_0_6px_currentColor]' : ''} flex-shrink-0`} />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-white uppercase tracking-wider">
                            {n.name}
                            <span className="ml-2 text-[10px] text-gray-500 font-normal">chain {n.chain_id}</span>
                          </div>
                          <div className="text-[11px] text-white/45 mt-0.5">{stateLabel}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-right md:text-right shrink-0">
                        <div>
                          <div className="text-[9px] uppercase tracking-widest text-gray-500">Head</div>
                          <div className="text-xs font-mono text-white/85">{n.head_block?.toLocaleString?.() ?? '-'}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-widest text-gray-500">Synced</div>
                          <div className="text-xs font-mono text-white/85">{n.last_synced_block?.toLocaleString?.() ?? '-'}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-widest text-gray-500">Events</div>
                          <div className="text-xs font-mono text-white/85">{n.total_events ?? 0}</div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-5 py-4 text-xs text-white/40">Reading indexer state…</div>
              )}
            </div>

            {indexer?.events_by_type && Object.values(indexer.events_by_type).some((v) => v > 0) && (
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-white/50" data-testid="indexer-event-mix">
                <span className="text-white/40 uppercase tracking-widest text-[9px]">Event mix:</span>
                {Object.entries(indexer.events_by_type)
                  .filter(([, v]) => v > 0)
                  .map(([k, v]) => (
                    <span key={k} className="text-white/70">
                      {k}: <span className="text-white/90 font-mono">{v}</span>
                    </span>
                  ))}
              </div>
            )}

            {/* Verify endpoint demand: the "Mastercard swipe" meter. */}
            <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="verify-demand">
              <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
                <div className="text-xs font-semibold text-white/85 uppercase tracking-[0.15em]">
                  Credential verify · network demand
                </div>
                <div className="text-[10px] text-white/40">
                  Aggregate only. No PII. No per-merchant identity.
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-0 divide-x divide-white/[0.06]">
                <div className="px-5 py-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Today</div>
                  <div className="text-xl font-bold text-white tabular-nums" data-testid="verify-today">
                    {verifyPulse ? verifyPulse.verifies_today?.toLocaleString?.() : '-'}
                  </div>
                </div>
                <div className="px-5 py-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Last 7 days</div>
                  <div className="text-xl font-bold text-white tabular-nums" data-testid="verify-7d">
                    {verifyPulse ? verifyPulse.verifies_last_7d?.toLocaleString?.() : '-'}
                  </div>
                </div>
                <div className="px-5 py-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Unique merchants · 7d</div>
                  <div className="text-xl font-bold text-white tabular-nums" data-testid="verify-domains-7d">
                    {verifyPulse ? verifyPulse.unique_domains_last_7d?.toLocaleString?.() : '-'}
                  </div>
                </div>
                <div className="px-5 py-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Valid rate · 7d</div>
                  <div className="text-xl font-bold text-white tabular-nums" data-testid="verify-validrate-7d">
                    {verifyPulse?.valid_rate_last_7d === null || verifyPulse?.valid_rate_last_7d === undefined
                      ? '-'
                      : `${(verifyPulse.valid_rate_last_7d * 100).toFixed(1)}%`}
                  </div>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-white/[0.06] text-[11px] text-white/50">
                Every call to <code className="text-white/70">/api/kypa/credential/verify</code> is a merchant
                choosing to gate their traffic on a KYPA credential. This meter is Kojiru&rsquo;s{' '}
                <em>demand signal</em> visible to anyone, priced by no one, today.
              </div>
            </div>
          </div>

          {/* ── Honest Claims (truth-feed from /api/honest-status/live) ────────── */}
          <div className="mt-12" data-testid="honest-claims-section">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-sm font-semibold text-white/90 uppercase tracking-[0.18em]">
                Honest claims · live
              </h2>
              <span className="text-[10px] text-white/40">
                Backed by <code className="text-white/55">/api/honest-status/live</code>
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-4 leading-relaxed">
              Every claim below reflects the platform&apos;s <strong className="text-white/70">actual</strong> state.
              No row says &quot;true&quot; unless the underlying component reports the precondition green.
              A red dot doesn&apos;t mean broken: it means the corresponding feature isn&apos;t yet wired (env var unset,
              contract not deployed, no real driver, etc.).
            </p>
            {honestErr ? (
              <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 px-4 py-3 text-xs text-rose-300" data-testid="honest-claims-error">
                Could not read honest-status feed: {honestErr}. Treat all honest-claim states below as unknown.
              </div>
            ) : !honest ? (
              <div className="text-xs text-white/40 animate-pulse">Reading truth feed…</div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="honest-claims-list">
                {Object.entries(honest.claims || {}).map(([key, value], idx) => {
                  const labels = {
                    withdrawal_guard_live: 'WithdrawalGuard deployed + enforcing',
                    withdrawal_guard_protects_real_tvl: 'WithdrawalGuard protects real lender TVL',
                    artifacts_pinned_to_real_ipfs: 'Artifacts pinned to real IPFS',
                    artifacts_carry_signed_attestations: 'Artifacts carry EIP-712 evaluator signatures',
                    knowledge_graph_anchored_on_base: 'KG Merkle root committed to Base mainnet',
                  };
                  const subtext = {
                    withdrawal_guard_live: `${honest.withdrawal_guard?.address ?? ''} · ${honest.withdrawal_guard?.enforcing ? 'enforcing' : 'idle'} · ${honest.withdrawal_guard?.total_epochs_elapsed ?? 0} epochs elapsed`,
                    withdrawal_guard_protects_real_tvl: 'Requires a lender pool that calls WithdrawalGuard.attemptWithdrawal(): see /app/memory/LENDER_POOL_SPEC_V0.md',
                    artifacts_pinned_to_real_ipfs: honest.ipfs_pinning_driver?.is_real_pinning
                      ? `Driver: ${honest.ipfs_pinning_driver?.provider}, available: ${honest.ipfs_pinning_driver?.available}`
                      : 'No IPFS driver wired (IPFS_PINNING_PROVIDER env var unset). Synthetic CIDs returned in API.',
                    artifacts_carry_signed_attestations: honest.artifact_attestation_signer?.signer_configured
                      ? `Signer: ${honest.artifact_attestation_signer?.signer_address}`
                      : 'No signer key configured (ARTIFACT_ATTESTATION_PRIVATE_KEY unset). Artifacts ship unsigned.',
                    knowledge_graph_anchored_on_base: honest.knowledge_anchor?.deployed
                      ? (honest.knowledge_anchor?.has_first_commit
                          ? `Anchor deployed · last epoch ${honest.knowledge_anchor?.latest_epoch} · ${honest.knowledge_anchor?.total_commitments} commits`
                          : 'Anchor contract deployed but no commits yet')
                      : 'Anchor contract not deployed (KNOWLEDGE_ANCHOR_ADDRESS env var unset)',
                  };
                  return (
                    <div
                      key={key}
                      className={`flex items-start justify-between px-5 py-4 ${idx > 0 ? 'border-t border-white/[0.06]' : ''}`}
                      data-testid={`honest-claim-${key}`}
                    >
                      <div className="flex items-start gap-3 min-w-0 pr-4">
                        <span className={`mt-1.5 inline-block w-2 h-2 rounded-full flex-shrink-0 ${value ? 'bg-emerald-400 shadow-[0_0_6px_currentColor]' : 'bg-rose-400/70'}`} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-white/85 leading-tight">{labels[key] || key}</div>
                          <div className="mt-1 text-[11px] text-white/40 leading-relaxed break-words">
                            {subtext[key] || ''}
                          </div>
                        </div>
                      </div>
                      <div className={`text-xs font-semibold whitespace-nowrap ${value ? 'text-emerald-300' : 'text-rose-300'}`}>
                        {value ? 'TRUE' : 'FALSE'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {honest?.knowledge_graph && (
              <div className="mt-4 text-[11px] text-white/35 leading-relaxed" data-testid="kg-counters">
                Knowledge Graph (off-chain):{' '}
                <span className="text-white/55">{honest.knowledge_graph.nodes}</span> nodes ·{' '}
                <span className="text-white/55">{honest.knowledge_graph.edges}</span> edges ·{' '}
                <span className="text-white/55">{honest.knowledge_graph.artifacts}</span> artifacts ·{' '}
                <span className="text-white/55">{honest.knowledge_graph.real_ipfs_pinned}</span> pinned ·{' '}
                <span className="text-white/55">{honest.knowledge_graph.signed_attestations}</span> signed
              </div>
            )}
          </div>

          <div className="mt-10 text-xs text-gray-500 leading-relaxed">
            <p>
              Kojiru AI publishes this status page in real time. Checks hit the production API gateway, Mongo health
              endpoint, and Base mainnet block reads. For incident history or operational disclosures, see Wayne Faulkner: {' '}
              <a href="mailto:wayne@kojiru.com" className="text-blue-400 hover:underline">wayne@kojiru.com</a>.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
